import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type {
  FilterFormTemplatePayload,
  FormTemplateDto,
  FormTemplateScope,
  FormTemplateSummary,
  SaveFormTemplatePayload,
  TemplateSnapshotDto,
} from '@/types/formTemplate';
import type { ListFilterPayload } from '@/types/filters';

// ============================================
// Query Params
// ============================================

export interface GetFormTemplatesParams {
  page?: number;
  size?: number;
  sort?: string;
  scope?: FormTemplateScope;
}

// ============================================
// Form Templates CRUD
// ============================================

export const getFormTemplates = async (
  params: GetFormTemplatesParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<FormTemplateSummary>> => {
  const response = await api.post<PaginatedResponse<FormTemplateSummary>>(
    '/form-service/v1/form-templates/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const getFormTemplateById = async (id: string): Promise<FormTemplateDto> => {
  const response = await api.get<FormTemplateDto>(`/form-service/v1/form-templates/${id}`);
  return response.data;
};

/** Get template by objectType + objectId + scope (instead of MongoDB ID) */
export const getFormTemplateByScope = async (
  objectType: string,
  objectId: string,
  scope: FormTemplateScope,
  queryParams?: { countryId?: string; employmentTypeId?: string },
): Promise<FormTemplateDto> => {
  const response = await api.get<FormTemplateDto>(
    `/form-service/v1/form-templates/${objectType}/${objectId}/${scope}`,
    { params: queryParams },
  );
  return response.data;
};

export const createFormTemplate = async (
  data: SaveFormTemplatePayload,
): Promise<FormTemplateDto> => {
  const response = await api.post<FormTemplateDto>('/form-service/v1/form-templates', data, {
    successToast: true,
  });
  return response.data;
};

export const updateFormTemplate = async (
  id: string,
  data: SaveFormTemplatePayload,
): Promise<FormTemplateDto> => {
  const response = await api.put<FormTemplateDto>(`/form-service/v1/form-templates/${id}`, data, {
    successToast: true,
  });
  return response.data;
};

/** Update template by objectType + objectId + scope (instead of MongoDB ID) */
export const updateFormTemplateByScope = async (
  objectType: string,
  objectId: string,
  scope: FormTemplateScope,
  data: SaveFormTemplatePayload,
  queryParams?: { countryId?: string; employmentTypeId?: string },
): Promise<FormTemplateDto> => {
  const response = await api.put<FormTemplateDto>(
    `/form-service/v1/form-templates/${objectType}/${objectId}/${scope}`,
    data,
    { params: queryParams, successToast: true },
  );
  return response.data;
};

export const deleteFormTemplate = async (id: string): Promise<void> => {
  await api.delete(`/form-service/v1/form-templates/${id}`, { successToast: true });
};

// ============================================
// Filter — filtered view by dimensions & attributes
// ============================================

/**
 * Filter a form template by dimension values and attribute flags.
 * Replaces the old `resolveFormTemplate` endpoint.
 *
 * @example
 * // Get mandatory forms for a specific country
 * filterFormTemplate(id, {
 *   filters: { country: ['uuid-1'] },
 *   attributes: { isMandatory: true },
 * });
 */
export const filterFormTemplate = async (
  id: string,
  payload: FilterFormTemplatePayload = {},
): Promise<FormTemplateDto> => {
  const response = await api.post<FormTemplateDto>(
    `/form-service/v1/form-templates/${id}/filter`,
    payload,
  );
  return response.data;
};

// ============================================
// Template Snapshots — tenant/entity-specific copies
// ============================================

/**
 * Get template snapshot for a specific entity.
 * Returns null if no snapshot exists (204 response).
 */
export const getTemplateSnapshot = async (
  objectType: string,
  objectId: string,
): Promise<TemplateSnapshotDto | null> => {
  const response = await api.get<TemplateSnapshotDto>(
    `/form-service/v1/template-snapshots/${objectType}/${objectId}`,
    {
      validateStatus: (status) => status === 200 || status === 204,
    },
  );
  if (response.status === 204) return null;
  return response.data;
};

/**
 * Filter template snapshot by dimension values and attribute flags.
 */
export const filterTemplateSnapshot = async (
  objectType: string,
  objectId: string,
  payload: FilterFormTemplatePayload = {},
): Promise<TemplateSnapshotDto | null> => {
  const response = await api.post<TemplateSnapshotDto>(
    `/form-service/v1/template-snapshots/${objectType}/${objectId}/filter`,
    payload,
    {
      validateStatus: (status) => status === 200 || status === 204,
    },
  );
  if (response.status === 204) return null;
  return response.data;
};
